'use client';
import type { UntypedFunction } from '@mnd-frontend/ts';
import {
  GREY_100,
  GREY_400,
  GREY_500,
  GREY_1000,
  NEUTRAL_WHITE,
  PURPLE_400,
  RED_900,
  SHADOW_1,
} from '@mnd-frontend/ui/shared/tokens';
import { type ReactElement, type ReactNode, useState } from 'react';
import { Menu, MenuItem, MenuTrigger, Popover, Pressable, Separator } from 'react-aria-components';
import styled from 'styled-components';
import { Button } from '../Button';
import Icons from '../Icons';
import type { ToolTipOptions } from '../Tooltip';
import ToolTip from '../Tooltip';

const StyledMenu = styled(Menu)`
  padding: 4px 0;
  min-width: 208px;
  background-color: ${NEUTRAL_WHITE};
  border: 2px solid ${PURPLE_400};
  border-radius: 8px;
  box-shadow: ${SHADOW_1};
`;

const StyledMenuItem = styled(MenuItem)<{
  $variant?: string;
  $disabled?: boolean;
}>`
  outline: none;
  cursor: pointer;
  font-size: 14px;
  color: ${props => {
    if (props.$disabled) return GREY_500;
    if (props.$variant === 'primary') return RED_900;
    return GREY_1000;
  }};
  opacity: ${props => (props.$disabled ? 0.6 : 1)};

  &:hover,
  &[data-focused] {
    background-color: ${props => (props.$disabled ? 'transparent' : GREY_100)};
  }

  ${props =>
    props.$disabled &&
    `
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const MenuItemContent = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px 16px;
`;

const StyledSeparator = styled(Separator)`
  height: 1px;
  background-color: ${GREY_400};
`;

type DividerOption = { divider: boolean };

type Option = {
  text: string;
  action: UntypedFunction;
  testid?: string;
  variant?: string;
  disabled?: boolean;
  tooltip?: ToolTipOptions;
  icon?: keyof typeof Icons | ReactNode;
};

export type DropdownOption = DividerOption | Option;

export const isDivider = (value: DropdownOption): value is DividerOption => 'divider' in value;

const renderIcon = (icon: keyof typeof Icons | ReactNode): ReactNode => {
  if (!icon) return null;

  if (typeof icon === 'string') {
    const IconComponent = Icons[icon as keyof typeof Icons];
    if (IconComponent) {
      return <IconComponent $size="xxs" />;
    }
  }

  if (typeof icon === 'object') {
    return icon;
  }

  return null;
};

export interface DropdownProps {
  title?: string;
  options?: DropdownOption[];
  hideTitle?: boolean;
  direction?: 'up' | 'down';
  children?: React.ReactNode;
  disabled?: boolean;
  textDropdown?: boolean;
  trigger?: ReactElement;
}

const Dropdown = ({
  title,
  options = [],
  hideTitle = false,
  children = undefined,
  direction = 'down',
  disabled = false,
  textDropdown = false,
  trigger,
  ...props
}: DropdownProps) => {
  const [open, setOpen] = useState<boolean>(false);

  if (options.length === 0 && !children) {
    return null;
  }

  const getTrigger = () => {
    if (trigger) {
      return trigger;
    }
    if (hideTitle || !title) {
      return (
        <Button
          type="button"
          configuration="toned"
          icon={<Icons.MoreHorizontal />}
          aria-label={title || 'Menu'}
          data-testid={'toggle-dropdown'}
          disabled={disabled}
          onClick={() => {}} // Required by <Button />, the actual click is handled by <Pressable />
          {...props}
        />
      );
    }
    return (
      <Button
        configuration={textDropdown ? 'text' : 'toned'}
        type="button"
        disabled={disabled}
        data-testid={'toggle-dropdown'}
        trailingIcon={textDropdown ? <Icons.ChevronDown /> : undefined}
        onClick={() => {}} // Required by <Button />, the actual click is handled by <Pressable />
        {...props}
      >
        {title}
      </Button>
    );
  };

  const placement = direction === 'up' ? 'top right' : 'bottom right';

  return (
    <MenuTrigger>
      <Pressable onPress={() => setOpen(o => !o)} isDisabled={disabled}>
        {getTrigger()}
      </Pressable>
      <Popover
        onOpenChange={isOpen => setOpen(isOpen)}
        isOpen={open}
        placement={placement}
        containerPadding={50}
      >
        <StyledMenu>
          {children ||
            options.map((option, index) =>
              isDivider(option) && option.divider === true ? (
                <StyledSeparator key={index} />
              ) : !isDivider(option) ? (
                <StyledMenuItem
                  key={index}
                  onAction={() => {
                    setOpen(false);
                    option.action();
                  }}
                  data-testid={option.testid || 'dropdown-option'}
                  isDisabled={option.disabled}
                  $variant={option.variant}
                  $disabled={option.disabled}
                >
                  {option.tooltip ? (
                    <ToolTip {...option.tooltip}>
                      <MenuItemContent>
                        {renderIcon(option.icon)}
                        {option.text}
                      </MenuItemContent>
                    </ToolTip>
                  ) : (
                    <MenuItemContent>
                      {renderIcon(option.icon)}
                      {option.text}
                    </MenuItemContent>
                  )}
                </StyledMenuItem>
              ) : null,
            )}
        </StyledMenu>
      </Popover>
    </MenuTrigger>
  );
};

export default Dropdown;
